import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";

import { useAuth } from "@/core/auth";
import { Button, Card } from "@/design-system";
import { DashboardLayout } from "@/layout/DashboardLayout";

import { CompanyCombobox } from "./components/CompanyCombobox";
import { UsageOverview } from "./components/UsageOverview";
import { UsageSeriesChart } from "./components/UsageSeriesChart";
import { UsageRanking } from "./components/UsageRanking";
import {
  useUsageByCompanyQuery,
  useUsageSeriesQuery,
} from "./hooks/useUsageQuery";

/** Atalhos de período. 30 dias é o padrão porque é o ciclo de cobrança. */
const PERIODOS = [
  { label: "7 dias", dias: 7 },
  { label: "30 dias", dias: 30 },
  { label: "90 dias", dias: 90 },
] as const;

function diaISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function periodoDe(dias: number): { from: string; to: string } {
  const hoje = new Date();
  const inicio = new Date(hoje);
  inicio.setUTCDate(inicio.getUTCDate() - (dias - 1));
  return { from: diaISO(inicio), to: diaISO(hoje) };
}

function formatarPeriodo(iso: string): string {
  return iso.split("-").reverse().join("/");
}

/**
 * Consumo de infraestrutura por cliente.
 *
 * **Por que esta tela existe.** A fatura da Azure é por recurso, não por
 * inquilino — o banco é um só, a API é uma só. Não existe "custo do cliente X"
 * na cobrança. A única forma de saber quem consome mais é pelo volume que cada
 * empresa gera, e esse dado só existe na aplicação.
 *
 * **O que ela responde.** Não "quanto o cliente X custou" — isso seria rateio
 * disfarçado de medição. Responde quem está puxando o crescimento que vai
 * obrigar a infraestrutura a crescer de novo.
 *
 * **Filtro no cliente.** A consulta traz sempre todas as empresas; escolher uma
 * só esconde as outras da tela. Filtrar no servidor quebrava o seletor — a
 * resposta vinha com uma empresa e a lista perdia as demais — e não economizava
 * nada, porque a agregação varre a mesma tabela de qualquer jeito.
 *
 * Métricas de infraestrutura (CPU, conexões, fila) não aparecem aqui: já estão
 * no painel do Azure Monitor, e não têm recorte por cliente — CPU do banco é
 * 30%, não "30% da empresa X".
 */
export function UsagePage() {
  const { user } = useAuth();
  const [dias, setDias] = useState<number>(30);
  const [companyId, setCompanyId] = useState<string | undefined>();

  const periodo = useMemo(() => periodoDe(dias), [dias]);

  const {
    data: consumo,
    isLoading: carregandoLista,
    isFetching: buscandoLista,
    refetch: recarregarLista,
  } = useUsageByCompanyQuery(periodo);
  const {
    data: serie,
    isLoading: carregandoSerie,
    isFetching: buscandoSerie,
    refetch: recarregarSerie,
  } = useUsageSeriesQuery({ ...periodo, companyId });

  // Memorizado porque `?? []` cria um array novo a cada render, o que faria os
  // `useMemo` seguintes recalcularem sempre — inclusive quando nada mudou.
  const todas = useMemo(() => consumo?.data ?? [], [consumo?.data]);

  // Uma flag para a tela inteira. Sem isso, cada bloco terminava de carregar em
  // momento diferente: números apareciam, o gráfico pulava, os cartões
  // empurravam o resto para baixo. A leitura é comparativa — meia tela pronta
  // engana mais que tela vazia.
  const carregando = carregandoLista || carregandoSerie;
  const atualizando = buscandoLista || buscandoSerie;

  const visiveis = useMemo(
    () => (companyId ? todas.filter((r) => r.companyId === companyId) : todas),
    [todas, companyId],
  );

  // Mesmo guard do cliente que os logs de acesso de suporte usam. A rota já
  // exige SUPERADMIN no backend; isto evita a tela piscar antes do 403.
  if (user && user.role !== "SUPERADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout title="Consumo por cliente">
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Empresa
              </span>
              {/* A lista vem de `todas`, nunca de `visiveis` — senão, com uma
                  empresa filtrada, o seletor perderia as outras e não daria
                  para trocar sem limpar o filtro. */}
              <CompanyCombobox
                opcoes={todas}
                valor={companyId}
                onChange={setCompanyId}
                disabled={carregando}
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Período
              </span>
              <div className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                {PERIODOS.map((p) => (
                  <Button variant="ghost" size="sm"
                    key={p.dias}
                    type="button"
                    onClick={() => setDias(p.dias)}
                    aria-pressed={dias === p.dias}
                    className={`rounded-md px-3 text-sm transition-colors ${
                      dias === p.dias
                        ? "bg-violet-600 text-white"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void recarregarLista();
              void recarregarSerie();
            }}
            disabled={atualizando}
            isLoading={atualizando}
          >
            <RefreshCw size={15} className="mr-1.5" />
            Atualizar
          </Button>
        </div>

        <UsageOverview rows={visiveis} isLoading={carregando} dias={dias} />

        <Card>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-medium text-gray-900">
              {companyId
                ? "Volume diário desta empresa"
                : "Volume diário da plataforma"}
            </h2>
            <span className="text-xs text-gray-500">
              {formatarPeriodo(periodo.from)} a {formatarPeriodo(periodo.to)}
            </span>
          </div>
          <UsageSeriesChart points={serie ?? []} isLoading={carregando} />
        </Card>

        {/*
          Sem filtro, o ranking é a lista — uma linha por empresa em vez de um
          cartão. Com duzentos clientes, cartão repetiria dez rótulos duzentas
          vezes para mostrar o que o resumo do topo já agregou.

          Com empresa escolhida, a tela inteira já é o detalhe dela: o resumo, o
          gráfico e o ranking passam a falar só dessa empresa. Não há o que
          listar.
        */}
        {!companyId && (
          <Card>
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-medium text-gray-900">
                Empresas por volume
              </h2>
              <span className="text-xs text-gray-500">
                mensagens · espaço · participação
              </span>
            </div>
            <UsageRanking
              rows={visiveis}
              isLoading={carregando}
              onSelectCompany={setCompanyId}
            />
          </Card>
        )}

        <p className="text-xs text-gray-500">
          Mensagens, conversas, contatos e agendamentos vêm do banco no momento
          da consulta — o período já inclui o movimento de hoje. Arquivos é o
          espaço ocupado agora no Blob Storage, sem recorte de período. Banco de
          dados é <strong>estimativa</strong>: linhas da empresa multiplicadas
          pelo tamanho médio de cada tabela, já que o Postgres não separa bytes
          por inquilino — serve para comparar ordens de grandeza, não para
          cobrar por byte.
        </p>
      </div>
    </DashboardLayout>
  );
}
